import { useMemo } from 'react';
import { usePricing } from '../../contexts/PricingContext';
import { WAFFLE_TYPES } from '../../utils/constants';

/**
 * Hook for calculating quote details based on sensor counts and pricing settings
 * 
 * @param {Object} sensorCounts - Object containing the counts of each sensor type
 * @param {number} sensorCounts.entry - Number of entry sensors
 * @param {number} sensorCounts.oa - Number of open area sensors
 * @param {number} sensorCounts.waffleRoomFlex - Number of waffle room/flex sensors
 * @param {number} sensorCounts.waffleDeskPhone - Number of waffle desk/phone booths
 * @param {number} discountPercentage - Optional discount percentage to apply
 * @returns {Object} Quote calculation results
 */
export const useQuoteCalculator = (sensorCounts, discountPercentage = 0) => {
  const { pricingData, isLoading } = usePricing();
  
  // For debugging
  console.log('Pricing context:', { pricingData, isLoading });
  
  // Calculate totals for each section
  const quoteDetails = useMemo(() => {
    if (!pricingData) {
      console.error('No pricing data available');
      return {
        sections: {},
        hardware: { items: [], total: 0 },
        software: { items: [], total: 0 },
        installation: { items: [], total: 0 },
        deployment: { items: [], total: 0 },
        support: { items: [], total: 0 },
        totalOneTime: 0,
        totalRecurring: 0,
        discount: 0,
        grandTotalOneTime: 0,
        grandTotalRecurring: 0,
        totalSensors: 0
      };
    }
    
    const { sections } = pricingData;
    // Provide fallback values in case sensorCounts is undefined or missing properties
    const { 
      entry = 0, 
      oa = 0, 
      waffleRoomFlex = 0, 
      waffleDeskPhone = 0 
    } = sensorCounts || {};
    
    // Helper function to calculate section totals
    const calculateSectionTotal = (section) => {
      if (!sections[section]) return { items: [], total: 0 };
      
      const sectionPricing = pricingData[section];
      
      // For debugging - log section pricing data
      console.log(`Section ${section} pricing:`, sectionPricing);
      
      // Create base items common across sections
      const baseItems = [
        {
          name: 'Entry Sensor',
          quantity: entry,
          unitPrice: sectionPricing?.entry?.price || 0,
          total: entry * (sectionPricing?.entry?.price || 0)
        },
        {
          name: 'Open Area Sensor',
          quantity: oa,
          unitPrice: sectionPricing?.oa?.price || 0,
          total: oa * (sectionPricing?.oa?.price || 0)
        }
      ];
      
      // Handle waffle sensors differently for software vs. other sections
      let items;
      if (section === 'software') {
        // For software section, create separate items for room/flex and desk/phone waffles
        items = [
          ...baseItems,
          {
            name: 'Waffle (rooms/flex/phone)',
            quantity: waffleRoomFlex,
            unitPrice: sectionPricing?.waffleMeeting?.price || 0,
            total: waffleRoomFlex * (sectionPricing?.waffleMeeting?.price || 0)
          },
          {
            name: 'Waffle (desks)',
            quantity: waffleDeskPhone,
            unitPrice: sectionPricing?.waffleDesk?.price || 0,
            total: waffleDeskPhone * (sectionPricing?.waffleDesk?.price || 0)
          }
        ];
      } else if (section === 'support') {
        // For setup services (support), use separate pricing for each type
        items = [
          ...baseItems,
          {
            name: 'Waffle (rooms/flex)',
            quantity: waffleRoomFlex,
            unitPrice: sectionPricing?.waffleMeeting?.price || 0,
            total: waffleRoomFlex * (sectionPricing?.waffleMeeting?.price || 0)
          },
          {
            name: 'Waffle (desks/phone booths)',
            quantity: waffleDeskPhone,
            unitPrice: sectionPricing?.waffleDesk?.price || 0,
            total: waffleDeskPhone * (sectionPricing?.waffleDesk?.price || 0)
          }
        ];
      } else {
        // For hardware and other sections, combine all waffle counts
        items = [
          ...baseItems,
          {
            name: 'Waffle Sensor',
            quantity: waffleRoomFlex + waffleDeskPhone,
            unitPrice: sectionPricing?.waffleDesk?.price || 0,
            total: (waffleRoomFlex + waffleDeskPhone) * (sectionPricing?.waffleDesk?.price || 0)
          }
        ];
      }
      
      const sectionTotal = items.reduce((sum, item) => sum + item.total, 0);
      
      return {
        items,
        total: sectionTotal
      };
    };
    
    // Calculate section totals
    const hardware = calculateSectionTotal('hardware');
    const software = calculateSectionTotal('software');
    const installation = calculateSectionTotal('installation');
    const deployment = calculateSectionTotal('deployment');
    const support = calculateSectionTotal('support');
    
    // Calculate one-time and recurring costs
    const oneTimeCosts = [
      ...(sections.hardware ? [hardware.total] : []),
      ...(sections.installation ? [installation.total] : []),
      ...(sections.deployment ? [deployment.total] : [])
    ];
    
    const recurringCosts = [
      ...(sections.software ? [software.total] : []),
      ...(sections.support ? [support.total] : [])
    ];
    
    // Calculate grand totals
    const totalOneTime = oneTimeCosts.reduce((sum, cost) => sum + cost, 0);
    const totalRecurring = recurringCosts.reduce((sum, cost) => sum + cost, 0);
    
    // Comment out discount calculation - set to 0
    /*
    // Apply discount if specified
    const discount = discountPercentage > 0 ?
      (totalOneTime + totalRecurring) * (discountPercentage / 100) : 0;
    
    const grandTotalOneTime = totalOneTime - (discount * (totalOneTime / (totalOneTime + totalRecurring)));
    const grandTotalRecurring = totalRecurring - (discount * (totalRecurring / (totalOneTime + totalRecurring)));
    */
    
    // Use totals without discount
    const discount = 0;
    const grandTotalOneTime = totalOneTime;
    const grandTotalRecurring = totalRecurring;
    
    return {
      sections: pricingData.sections,
      hardware,
      software,
      installation,
      deployment,
      support,
      totalOneTime,
      totalRecurring,
      discount,
      grandTotalOneTime,
      grandTotalRecurring,
      totalSensors: entry + oa + waffleRoomFlex + waffleDeskPhone
    };
  }, [pricingData, sensorCounts, discountPercentage]);
  
  return quoteDetails;
}; 